import React, { useState } from "react";

import useScrollToTop from "../../../hooks/useScollToTop";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import ScrollIndicator from "../../Home/ScrollIndicator";

import ParticipateVoteModal from "./ParticipateVoteModal";
import UserBallotAnswersModal from "./UserBallotAnswersModal";

import VoteCard from "./components/VoteCard";
import VotesEmptyState from "./components/VotesEmptyState";
import VotesHero from "./components/VotesHero";
import VotesSkeleton from "./components/VotesSkeleton";
import VotesToolbar from "./components/VotesToolbar";
import { useUserVotesController } from "./hooks/useUserVotesController";
import { FaVoteYea } from "react-icons/fa";

export default function UserVotePage() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId || user?.id;

  const {
    loading,
    error,
    searchTerm,
    setSearchTerm,
    viewFilter,
    setViewFilter,
    filteredVotes,
    stats,
    hasBallot,
    fetchVotesAndBallots,
  } = useUserVotesController({ userId });

  const [selectedVoteId, setSelectedVoteId] = useState(null);
  const [openParticipateModal, setOpenParticipateModal] = useState(false);

  const [answersVoteId, setAnswersVoteId] = useState(null);
  const [openAnswersModal, setOpenAnswersModal] = useState(false);

  const handleParticipate = (voteId) => {
    setSelectedVoteId(voteId);
    setOpenParticipateModal(true);
  };

  const handleCloseParticipateModal = () => {
    setSelectedVoteId(null);
    setOpenParticipateModal(false);
  };

  const handleViewAnswers = (voteId) => {
    setAnswersVoteId(voteId);
    setOpenAnswersModal(true);
  };

  const handleCloseAnswersModal = () => {
    setAnswersVoteId(null);
    setOpenAnswersModal(false);
  };

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Utilisateur"
          title="Votes & Sondages"
          subtitle="Participe aux votes et sondages créés par le club."
          icon={FaVoteYea}
          align="left"
          className="mb-4"
        />

        <div className="mb-5 sm:mb-7">
          <DashboardNavigation role={userRole} />
        </div>

        <VotesHero
          totalCount={stats.total}
          pendingCount={stats.pending}
          answeredCount={stats.answered}
          filteredCount={stats.filtered}
        />

        <div className="mt-4 overflow-hidden rounded-[2rem] border border-white/70 bg-white/85 shadow-xl shadow-slate-200/70 backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/70 dark:shadow-black/20 sm:mt-6">
          <div className="border-b border-slate-200/70 bg-white/70 px-3 py-3 dark:border-slate-800 dark:bg-slate-900/60 sm:px-5">
            <VotesToolbar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              viewFilter={viewFilter}
              onViewFilterChange={setViewFilter}
              loading={loading}
              onRefresh={fetchVotesAndBallots}
              stats={stats}
            />
          </div>

          <div className="p-3 sm:p-5 lg:p-6">
            {loading && <VotesSkeleton />}

            {!loading && error && (
              <VotesEmptyState
                variant="error"
                title="Impossible de charger les votes"
                description={error}
                actionLabel="Réessayer"
                onAction={fetchVotesAndBallots}
              />
            )}

            {!loading && !error && filteredVotes.length === 0 && (
              <VotesEmptyState
                title="Aucun vote actif"
                description={
                  searchTerm
                    ? "Aucun vote ne correspond à ta recherche."
                    : "Reviens plus tard ou vérifie si un vote est programmé."
                }
                actionLabel={searchTerm ? "Effacer la recherche" : "Actualiser"}
                onAction={
                  searchTerm ? () => setSearchTerm("") : fetchVotesAndBallots
                }
              />
            )}

            {!loading && !error && filteredVotes.length > 0 && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {filteredVotes.map((vote) => {
                  const ballot = hasBallot(vote.id);

                  return (
                    <VoteCard
                      key={vote.id}
                      vote={vote}
                      ballot={ballot}
                      onParticipate={() => handleParticipate(vote.id)}
                      onViewAnswers={() => handleViewAnswers(vote.id)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {openParticipateModal && selectedVoteId && (
        <ParticipateVoteModal
          isOpen={openParticipateModal}
          voteId={selectedVoteId}
          userId={userId}
          onClose={handleCloseParticipateModal}
          refreshVotes={fetchVotesAndBallots}
        />
      )}

      {openAnswersModal && answersVoteId && (
        <UserBallotAnswersModal
          isOpen={openAnswersModal}
          voteId={answersVoteId}
          userId={userId}
          onClose={handleCloseAnswersModal}
        />
      )}
    </section>
  );
}
